import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SessionStorage } from '@hotjar/insights-common/lib/storage-factory';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useSamplingState } from '@hotjar/webapp/hooks/useSamplingState';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { SITE } from '@hotjar/webapp/constants/routes';
import { useCurrentSite } from '@hotjar/site';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';

import { useStickyNotification } from '@hotjar/navigation';

function getSessionSettingsRoute(siteId: SiteId) {
  return getRoute(SITE.SETTINGS.path, {
    siteId,
  });
}

const getCaptureDisabledKey = (siteId?: number) => `sessionCaptureDisabledAlertDismissed-${siteId}`;
const getTrackingCodeIssueKey = (siteId?: number) => `trackingIssueAlertDismissed-${siteId}`;

export const useActivationAlerts = (): void => {
  const { show, hide } = useStickyNotification();
  const { t } = useTranslation();
  const { data: currentSite, isLoading } = useCurrentSite();
  const { show: openTrackingCodeModal } = useModalTrackingCode();
  const { click: trackClick } = useTrack();
  const { inDisabledState: sessionCaptureDisabled } = useSamplingState();
  const trackingCodeState = useTrackingCodeState();

  const [isCaptureDisabledDismissed, setIsCaptureDisabledDismissed] = useState(
    SessionStorage.get(getCaptureDisabledKey(currentSite?.id), false),
  );
  const [isTrackingIssueDismissed, setIsTrackingIssueDismissed] = useState(
    SessionStorage.get(getTrackingCodeIssueKey(currentSite?.id), false),
  );
  const [hasTrackingIssue, setHasTrackingIssue] = useState<boolean>(false);

  useEffect(() => {
    setIsCaptureDisabledDismissed(
      SessionStorage.get(getCaptureDisabledKey(currentSite?.id), false),
    );
    setIsTrackingIssueDismissed(
      SessionStorage.get(getTrackingCodeIssueKey(currentSite?.id), false),
    );
  }, [currentSite?.id]);

  useEffect(() => {
    setHasTrackingIssue(trackingCodeState?.installed && !trackingCodeState?.isActive);
  }, [trackingCodeState?.installed, trackingCodeState?.isActive]);

  const handleTrackingIssueDismiss = useCallback(() => {
    setIsTrackingIssueDismissed(true);
    SessionStorage.set(getTrackingCodeIssueKey(currentSite?.id), true);
    trackClick('Dismiss', 'Tracking Issue Alert', {});
  }, [currentSite?.id, trackClick]);

  const handleCaptureDisabledDismiss = useCallback(() => {
    setIsCaptureDisabledDismissed(true);
    SessionStorage.set(getTrackingCodeIssueKey(currentSite?.id), true);
    trackClick('Dismiss', 'Session Capture Disabled Alert', {});
  }, [currentSite?.id, trackClick]);

  const handleTrackingIssueAction = useCallback(() => {
    if (!currentSite?.id) {
      return;
    }

    trackClick('Verify Tracking Code Link', 'Tracking Issue Alert', {});
    openTrackingCodeModal(currentSite, 2, 'Tracking Issue Alert');
  }, [currentSite, openTrackingCodeModal, trackClick]);

  const handleCaptureDisabledAction = useCallback(() => {
    trackClick('Enable Session Capture Link', 'Session Capture Disabled Alert', {});
  }, [trackClick]);

  // tracking issue
  useEffect(() => {
    if (isTrackingIssueDismissed || isLoading || sessionCaptureDisabled || !hasTrackingIssue) {
      hide('tracking-issue');
    } else {
      show('tracking-issue', {
        isDismissible: true,
        kind: 'warning',
        actionText: t('activation:alerts.verify-tracking-code'),
        onAction: handleTrackingIssueAction,
        onDismiss: handleTrackingIssueDismiss,
        text: t('activation:alerts.tracking-issue'),
      });
    }
  }, [
    handleTrackingIssueAction,
    handleTrackingIssueDismiss,
    hasTrackingIssue,
    hide,
    isLoading,
    isTrackingIssueDismissed,
    sessionCaptureDisabled,
    show,
    t,
  ]);

  // session capture disabled
  useEffect(() => {
    if (
      isCaptureDisabledDismissed ||
      isLoading ||
      !trackingCodeState.installed ||
      !currentSite ||
      !sessionCaptureDisabled
    ) {
      hide('session-capture-disabled');
    } else {
      show('session-capture-disabled', {
        isDismissible: true,
        kind: 'warning',
        actionText: t('activation:alerts.enable-session-capture'),
        actionHref: getSessionSettingsRoute(currentSite.id),
        onAction: handleCaptureDisabledAction,
        onDismiss: handleCaptureDisabledDismiss,
        text: t('activation:alerts.session-capture-disabled'),
      });
    }
  }, [
    currentSite,
    handleCaptureDisabledAction,
    handleCaptureDisabledDismiss,
    hide,
    isCaptureDisabledDismissed,
    isLoading,
    sessionCaptureDisabled,
    show,
    t,
    trackingCodeState.installed,
  ]);
};
